import React, { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { CropperPreview } from "react-advanced-cropper";
import CropperWizard from './ImageCropping/CropperWizard';

import "./Cropping/Icons/Cropper.css";
import Cropper from "./Cropping/Cropper";
import piexif from "piexifjs";

const ImageCropModal = ({ openCropImageModal, image, imageRand, onClose, onSave }) => {
    const cropperRef = useRef(null);
    const cropperInstanceRef = useRef(null);
    const previewRef = useRef(null);
    const [showWizard, setShowWizard] = useState(false);
    const [stencilType, setStencilType] = useState("rectangle");
    const [aspectRatio, setAspectRatio] = useState('free');
    const [saving, setSaving] = useState(false);
    const [resetVersion, setResetVersion] = useState(0);
    const imageUrl = image && image.edited_image_path
        ? `${image.edited_image_path}?rand=${imageRand || ''}`
        : '';

  // ✅ Save cropped image
  const handleSave = async () => {
  if (saving) return;
  if (!cropperRef.current) {
    window.alert("Cropper is not ready yet. Please try again.");
    return;
  }
  if (typeof onSave !== "function") {
    window.alert("Save handler is not available.");
    return;
  }

  try {
    const canvas = cropperRef.current.getCanvas();
    if (!canvas) {
      window.alert("Unable to generate cropped image. Please adjust crop and try again.");
      return;
    }

    setSaving(true);

    // 1️⃣ Fetch original JPEG as binary string for piexifjs
    let exifBytes = null;
    try {
      const response = await fetch(imageUrl);
      const arrayBuffer = await response.arrayBuffer();

      // piexifjs needs a binary string, not ArrayBuffer
      const uint8 = new Uint8Array(arrayBuffer);
      let binaryStr = "";
      // Process in chunks to avoid call stack overflow on large images
      const CHUNK = 8192;
      for (let i = 0; i < uint8.length; i += CHUNK) {
        binaryStr += String.fromCharCode(...uint8.subarray(i, i + CHUNK));
      }

      // Load EXIF from original, update dimension + orientation fields
      const exifObj = piexif.load(binaryStr);

      // Update pixel dimensions to match the new cropped canvas size
      if (!exifObj["Exif"]) exifObj["Exif"] = {};
      exifObj["Exif"][piexif.ExifIFD.PixelXDimension] = canvas.width;
      exifObj["Exif"][piexif.ExifIFD.PixelYDimension] = canvas.height;

      // Reset orientation — the canvas is already correctly oriented
      if (!exifObj["0th"]) exifObj["0th"] = {};
      exifObj["0th"][piexif.ImageIFD.Orientation] = 1;

      // Remove thumbnail (IFD1) — it's stale after cropping
      exifObj["1st"] = {};
      exifObj["thumbnail"] = null;

      exifBytes = piexif.dump(exifObj);
    } catch (exifErr) {
      console.warn("EXIF extraction failed, saving without metadata:", exifErr);
    }

    // 2️⃣ Get cropped image as data URL
    const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.92);

    // 3️⃣ Inject EXIF back into the cropped JPEG data URL
    let finalDataUrl = croppedDataUrl;
    if (exifBytes) {
      try {
        finalDataUrl = piexif.insert(exifBytes, croppedDataUrl);
        console.log("[EXIF] Injection check:", {
          before_bytes: croppedDataUrl.length,
          after_bytes: finalDataUrl.length,
          diff_bytes: finalDataUrl.length - croppedDataUrl.length,
          // Should be ~1400 bytes difference if EXIF was injected. If 0, injection failed.
        });
      } catch (injectErr) {
        console.warn("EXIF re-injection failed, saving without metadata:", injectErr);
        finalDataUrl = croppedDataUrl;
      }
    }

    // 4️⃣ Convert data URL → Blob → File
    const [header, base64] = finalDataUrl.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const raw = atob(base64);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });

    const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
    console.log("[EXIF] File about to be sent to onSave:", {
      name: file.name,
      size_bytes: file.size,
      type: file.type,
      // Download this blob locally to test on jimpl.com manually
      preview_url: URL.createObjectURL(file),
    });
    // Replace: await onSave(file);
    console.log("[EXIF] Calling onSave with file, size:", file.size);
    const verifyBuffer = await file.arrayBuffer();
    const verifyBinary = arrayBufferToBinaryString(verifyBuffer);

    const verifyExif = piexif.load(verifyBinary);

    console.log("[FINAL FILE EXIF]", {
      orientation:
        verifyExif["0th"] &&
        verifyExif["0th"][piexif.ImageIFD.Orientation],

      pixelX:
        verifyExif["Exif"] &&
        verifyExif["Exif"][piexif.ExifIFD.PixelXDimension],

      pixelY:
        verifyExif["Exif"] &&
        verifyExif["Exif"][piexif.ExifIFD.PixelYDimension],
    });
    await onSave(file);
    console.log("[EXIF] onSave completed — if EXIF is missing now, it's stripped server-side");

  } catch (error) {
    console.error("Crop save failed:", error);
    window.alert("Unable to save cropped image. Please try again.");
  } finally {
    setSaving(false);
  }
};

  // Helper: ArrayBuffer → binary string (for piexifjs)
  function arrayBufferToBinaryString(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return binary;
  }

  // Helper: data URL → Blob (avoids toBlob callback complexity)
  function dataUrlToBlob(dataUrl) {
    const [header, base64] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }

  // ✅ Preview sync
  const onUpdate = (cropper) => {
    cropperInstanceRef.current = cropper;
    if (previewRef.current) {
      previewRef.current.update(cropper);
    }
  };

  // Keep preview in sync when right-side filter controls change state.
  useEffect(() => {
    const syncPreview = () => {
      if (previewRef.current && cropperInstanceRef.current) {
        previewRef.current.update(cropperInstanceRef.current);
      }
    };

    const rafId = requestAnimationFrame(syncPreview);
    return () => cancelAnimationFrame(rafId);
  }, [aspectRatio, stencilType, resetVersion]);

  // free / ratio parser
  const parseAspectRatio = (ratio) => {
    if (!ratio || ratio === "free") return undefined;

    if (typeof ratio === "number") return ratio;

    // This wizard option is a composite preset and doesn't map to one fixed ratio.
    if (ratio.includes("x")) return undefined;

    if (ratio.includes(":")) {
      const [w, h] = ratio.split(":").map(Number);
      if (!w || !h) return undefined;
      return w / h;
    }

    const numeric = Number(ratio);
    return Number.isNaN(numeric) ? undefined : numeric;
  };

  if (!openCropImageModal) return null;

  const aspectRatioOptions = ['free', '1:1', '1:2', '3:4', '2:1'];//, '1:2 x 2:1'
  const handleReset = () => {
    setAspectRatio('free');
    setStencilType('rectangle');
    setShowWizard(false);

    // Prefer native cropper reset to avoid visual jerk from remounting.
    if (cropperRef.current && typeof cropperRef.current.reset === "function") {
      cropperRef.current.reset();
      return;
    }

    // Fallback: undo current transforms in-place.
    if (cropperRef.current && typeof cropperRef.current.getTransforms === "function") {
      const transforms = cropperRef.current.getTransforms() || {};
      const rotate = transforms.rotate || 0;
      const flip = transforms.flip || {};

      if (rotate && typeof cropperRef.current.rotateImage === "function") {
        cropperRef.current.rotateImage(-rotate, {
          transitions: false,
          immediately: true,
          interaction: true,
        });
      }
      if ((flip.horizontal || flip.vertical) && typeof cropperRef.current.flipImage === "function") {
        cropperRef.current.flipImage(!!flip.horizontal, !!flip.vertical);
      }
      if (typeof cropperRef.current.transformImageEnd === "function") {
        cropperRef.current.transformImageEnd();
      }
      if (typeof cropperRef.current.refresh === "function") {
        cropperRef.current.refresh();
      }
    }

    // Hard fallback: rebuild cropper instance only (not whole modal)
    // to guarantee original state reset across all transforms/coordinates.
    setResetVersion((prev) => prev + 1);
  };

  return (
    <Modal
      backdrop={true}
      show={openCropImageModal}
      onHide={onClose}
      centered
      dialogClassName="ai-custom-modal ai-crop-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Crop Image</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row">

          {/* ================= LEFT SIDE - CROPPER ================= */}
          <div className="col-md-8">
            <button
                className="croppers-wizard__settings-button"
                onClick={() => setShowWizard(true)}
                style={{
                    position: "absolute",
                    top: 3,
                    right: 20
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="-4 -4 24 24"
                >
                    <path
                        fill="#FFF"
                        d="M1 2.2h-2.9V.6H1v1.6zm8.2 6.6H-1.9V7.2H9.2v1.6zm-6.6 6.6h-4.5v-1.7h4.5v1.7zM6.3-.3V3c0 .5-.4.8-.8.8H2.2c-.4.1-.8-.3-.8-.8V-.3c0-.5.4-.8.8-.8h3.3c.5 0 .8.4.8.8zM8 13v3.3c0 .5-.4.8-.8.8H3.9c-.5 0-.8-.4-.8-.8V13c0-.5.4-.8.8-.8h3.3c.4-.1.8.3.8.8zm9.9-10.8H6.8V.6h11.1v1.6zm0 13.2H8.4v-1.7h9.5v1.7zm-3.3-9.1v3.3c0 .5-.4.8-.8.8h-3.3c-.5 0-.8-.4-.8-.8V6.3c0-.5.4-.8.8-.8h3.3c.4 0 .8.4.8.8zm3.3 2.5H15V7.2h2.9v1.6z"
                    />
                </svg>
            </button>
            <Cropper
              ref={cropperRef}
              src={imageUrl}
              onUpdate={onUpdate}
              aspectRatio={parseAspectRatio(aspectRatio)}
              stencilType={stencilType}
              resetVersion={resetVersion}
              navigation={true}   // 🔥 THIS ENABLES YOUR NEW TOOLBAR
            />
            {
                showWizard &&
                <CropperWizard
                    show={showWizard}
                    onClose={() => setShowWizard(false)}
                    stencilType={stencilType}
                    setStencilType={setStencilType}
                    aspectRatio={aspectRatio}
                    setAspectRatio={setAspectRatio}
                />
            }

          </div>

          {/* ================= RIGHT SIDE ================= */}
          <div className="col-md-4">

            {/* Preview */}
            <CropperPreview
              ref={previewRef}
              className={`crop-preview ${stencilType === "circle" ? "crop-preview--circle" : ""}`}
            />

            <div className="crop-inline-filters">
              <div className="crop-inline-filters__group">
                <div className="crop-inline-filters__label">Aspect Ratio</div>
                <div className="crop-inline-filters__options">
                  {aspectRatioOptions.map((ratio) => (
                    <button
                      key={ratio}
                      type="button"
                      className={`crop-inline-filters__btn ${aspectRatio === ratio ? "crop-inline-filters__btn--active" : ""}`}
                      onClick={() => setAspectRatio(ratio)}
                    >
                      {ratio}
                    </button>
                  ))}
                </div>
              </div>

              <div className="crop-inline-filters__group">
                <div className="crop-inline-filters__label">Stencil Type</div>
                <div className="crop-inline-filters__options">
                  <button
                    type="button"
                    className={`crop-inline-filters__btn ${stencilType === "rectangle" ? "crop-inline-filters__btn--active" : ""}`}
                    onClick={() => setStencilType("rectangle")}
                  >
                    Rectangle
                  </button>
                  {/* <button
                    type="button"
                    className={`crop-inline-filters__btn ${stencilType === "circle" ? "crop-inline-filters__btn--active" : ""}`}
                    onClick={() => setStencilType("circle")}
                  >
                    Circle
                  </button> */}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="crop-actions center">
                <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSave}
                    disabled={saving}
                >
                {saving ? "Saving..." : "Save"}
                </button>

                <button
                    type="button"
                    className="btn-sm btn btn-default"
                    onClick={handleReset}
                    disabled={saving}
                >
                Reset
                </button>

                <button
                    className="btn btn-warning btn-sm"
                    onClick={onClose}
                >
                Cancel
                </button>
            </div>

          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ImageCropModal;